import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@supabaseClient';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { Tables } from '@supabaseTypes';
import securitiesTransactionService from "@securitiesTransactionService";

export type SecuritiesTransaction = Tables<'securities_transactions'>;

/**
 * Hook to fetch securities transactions for a specific security
 */
export function useFetchSecuritiesTransactions(securityType: SecurityType, securityId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['securities-transactions', securityType, securityId, user?.id],
    queryFn: async () => {
      return await securitiesTransactionService.fetchSecurityTransactionsForSecurity({
        securityId,
        securityType,
        userId: user.id
      });
    },
    enabled: !!user && !!securityId,
  });
}

/**
 * Hook to create a new securities transaction
 * This hook handles:
 * - Creating the transaction record
 * - Invalidating relevant queries to refresh data
 */
export function useCreateSecuritiesTransaction() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (transaction: Omit<SecurityTransactionCreateRequest, 'user_id'>) => {
      return await securitiesTransactionService.createSecurityTransaction({...transaction, userId: user.id});
    },
    onSuccess: (data) => {
      // Invalidate securities transactions queries
      qc.invalidateQueries({ 
        queryKey: ['securities-transactions', data.security_type, data.security_id] 
      });
      
      // Invalidate the specific security query based on type
      if (data.security_type === 'equity') {
        qc.invalidateQueries({ queryKey: ['equity-securities'] });
        qc.invalidateQueries({ queryKey: ['equity-security', data.security_id] });
      } else if (data.security_type === 'debt') {
        qc.invalidateQueries({ queryKey: ['debt-securities'] });
        qc.invalidateQueries({ queryKey: ['debt-security', data.security_id] });
      } else if (data.security_type === 'derivative') {
        qc.invalidateQueries({ queryKey: ['derivative-securities'] });
        qc.invalidateQueries({ queryKey: ['derivative-security', data.security_id] });
      }
      
      // Invalidate securities overview to update aggregate metrics
      qc.invalidateQueries({ queryKey: ['securities-overview'] });
      
      // Invalidate accounts query to update bank account balances
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Hook to fetch all securities transactions for the current user
 */
export function useAllSecuritiesTransactions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['securities-transactions', 'all', user?.id],
    queryFn: async () => {
      return await securitiesTransactionService.fetchAllSecurityTransactions({
        // TODO: include security type in the query
        userId: user.id,
      });
    },
    enabled: !!user,
  });
}
